import { AppProviders } from "@/components/AppProviders";
import { SubscriptionPurchaseProvider } from "@/contexts/SubscriptionPurchaseContext";
import { TaskFiltersProvider } from "@/contexts/TaskFiltersContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { useLazoUser } from "@/contexts/UserContext";
import { Colors } from "@/enums/Colors";
import { EMAIL_REDIRECT_PATHS } from "@/enums/EmailRedirectPaths";
import { LazoPaths } from "@/enums/LazoPaths";
import { PUBLIC_PATHS } from "@/enums/PublicPaths";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import environment from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/environment.json";
import {
  AuthEvents,
  default as authService,
  default as AuthService,
  LoginError,
} from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService, {
  NotificationEvents,
} from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { SnackBarData } from "@/external/ensolvers-core-frontend-mui-v5/types/SnackbarData";
import { useNavigationCount } from "@/hooks/useNavigationCount";
import { usePageViewTracker } from "@/hooks/usePageViewTracker";
import { LazoUser } from "@/models/LazoUser";
import ProtectedRoute from "@/routes/ProtectedRoute";
import { TenantAwareProtectedRoute } from "@/routes/TenantAwareProtectedRoute";
import LazoUserService from "@/services/LazoUserService";
import LazoUserSessionService, { LazoUserSessionEvents } from "@/services/LazoUserSessionService";
import LocalStorageWorkspacesAndTenantMemberships from "@/services/LocalStorageTenantMembership";
import { LazoRoutes, lazoRoutesSpecification } from "@/types/BaseProjectRoutes";
import { LAZO_APPLICATIONS_OPTIONS_SERVICE, LazoApplicationOptions } from "@/types/LazoApplicationOptions";
import { LocalStorageConstants } from "@/types/LocalStorageConstants";
import { SessionStorageConstants } from "@/types/SessionStorageConstants";
import { getLandingPageByUser } from "@/utils/NavigationUtils";
import { PostLoginRedirectStorageKey } from "@/utils/RedirectUtils";
import { sendSnackBarMessage } from "@/utils/SnackBarUtils";
import moment from "moment";
import { lazy, Suspense, useEffect, useState } from "react";
import { matchPath, Navigate, Outlet, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import Fallback from "../../routes/Fallback";
import { AppContainerWrapper } from "../AppContainerWrapper";
import { Logout } from "../auth/Logout";
import EnvOverlay from "../EnvOverlay";
import FiltersRoutesWrapper from "../FilterRoutesWrapper";

const GetStarted = lazy(() => import("../auth/GetStarted"));
const LazoOnboarding = lazy(() => import("../onboarding-components/LazoOnboarding"));
const DashboardDetails = lazy(() => import("@/components/mocks/DashboardDetails"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const UnauthorizedScreen = lazy(() => import("@/routes/UnauthorizedScreen"));
const UserManagement = lazy(() => import("../../external/ensolvers-core-frontend-mui-v5/components/Main/UserManagement"));
const StaticDashboard = lazy(() => import("../static-dashboard-components/StaticDashboard"));
const TermsAndPolicies = lazy(() => import("../terms-and-policies-components/TermsAndPolicies"));
const DashboardSelection = lazy(() => import("@/pages/DashboardSelection"));
const OnboardingPrototypeCompanyForm = lazy(() => import("../prototype-onboarding-components/OnboardingPrototypeCompanyForm"));
const TasksPage = lazy(() => import("@/pages/TasksPageV2"));
const NewTaskPage = lazy(() => import("@/pages/NewTaskPage"));
const AdminTasksPage = lazy(() => import("@/pages/AdminTasksPage"));
const TaskDetails = lazy(() => import("@/pages/TaskDetails"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const DeadLineList = lazy(() => import("../tax-deadlines-components/DeadLineList"));
const Fundraising = lazy(() => import("../fundraising/Fundraising"));
const CustomersPage = lazy(() => import("@/pages/CustomersPage"));
const CustomerDetailsPage = lazy(() => import("@/pages/CustomerDetailsPage"));
const FunderListDetail = lazy(() => import("../fundraising/detail-view/FunderListDetail"));
const UserInvitationCompany = lazy(() => import("@/pages/UserInvitationCompany"));
const PortalInboxPage = lazy(() => import("@/pages/PortalInboxPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const LazoOneCompanyAdd = lazy(() => import("@/pages/LazoOneCompanyAdd"));
const LazoOneTaskDetails = lazy(() => import("@/pages/LazoOneTaskDetailsV2"));
const EmailConfirmationStepComponent = lazy(() => import("@/pages/EmailConfirmationStepPage"));
const PersonalInformationPage = lazy(() => import("@/pages/PersonalInformationPage"));
const PendingRegistrationPage = lazy(() => import("@/pages/PendingRegistrationPage").then((m) => ({ default: m.PendingRegistrationPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordEmailSentPage = lazy(() => import("@/pages/ResetPasswordEmailSentPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const NotSupportedPage = lazy(() => import("@/pages/NotSupportedPage"));
const ProductManagerPage = lazy(() => import("@/pages/ProductManagerPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const VerifyingEmailPage = lazy(() => import("@/pages/VerifyingEmailPage"));
const ProductFlowTasksPage = lazy(() => import("@/pages/ProductFlowTasksPage"));
const WorkflowDetailV2Page = lazy(() => import("@/pages/WorkflowDetailV2Page"));
const LazoTeamPage = lazy(() => import("@/pages/LazoTeamPage"));
const OneDocumentCenterPage = lazy(() => import("@/pages/OneDocumentCenterPage"));
const CustomerDetailsAccountingPage = lazy(() => import("@/pages/CustomerDetailsAccountingPage"));
const LazoEmployeeDetails = lazy(() =>
  import("../lazo-team-page-components/lazo-employee-detail/LazoEmployeeDetails").then((m) => ({ default: m.LazoEmployeeDetails }))
);
const AdvancedSearch = lazy(() => import("../fundraising/advanced-search/AdvancedSearch"));
const AdminToolsPage = lazy(() => import("../admin-components/AdminToolsPage"));
const VirtualRoomAnalytics = lazy(() => import("../virtual-room-components/VirtualRoomAnalytics"));
const GuestUserShareLinkOnboarding = lazy(() => import("@/models/virtual-data-room/share-by-link-components/GuestUserShareLinkOnboarding"));
const SharedVirtualDataRoomsInsidePage = lazy(() => import("@/pages/SharedVirtualDataRoomsInsidePage"));
// const UserHome = lazy(() => import("../home-components/UserHome")); // Commented out - not being used, causes logout
const SharedCheckEmailPage = lazy(() => import("@/pages/SharedCheckEmailPage"));
const RoomExpiredPage = lazy(() => import("@/pages/RoomExpiredPage"));
const SharedVirtualDataRoomsFilePage = lazy(() => import("@/pages/SharedVirtualDataRoomsFilePage"));
const SharedDataRoomFinancialReportPage = lazy(() => import("@/pages/SharedDataRoomFinancialReportPage"));
const RedirectPage = lazy(() => import("@/pages/RedirectPage"));
const PaywallContent = lazy(() => import("../paywall/PaywallContent"));
const CreateCompanyOrIndividual = lazy(() => import("../add-workspace-components/CreateCompanyOrIndividualRoot"));
const LazoOneChatRoot = lazy(() => import("../communications-chat-components/LazoOneChatRoot"));
const OneVirtualDataRoomsFilePage = lazy(() => import("@/pages/OneVirtualDataRoomsFilePage"));
const OneVirtualDataRoomsPage = lazy(() => import("@/pages/OneVirtualDataRoomsPage"));
const OneVirtualDataRoomsCreatePage = lazy(() => import("@/pages/OneVirtualDataRoomsCreatePage"));
const OneVirtualDataRoomsInsidePage = lazy(() => import("@/pages/OneVirtualDataRoomsInsidePage"));
const VirtualDataRoomFinancialReportPage = lazy(() => import("@/pages/VirtualDataRoomFinancialReportPage"));
const OneDataRoomPage = lazy(() => import("@/pages/OneDataRoomPage"));
const OneDataRoomFilesPage = lazy(() => import("@/pages/OneDataRoomFilesPage"));
const OneSpendingPage = lazy(() => import("@/pages/OneSpendingPage"));
const OneSpendingDetailsPage = lazy(() => import("@/pages/OneSpendingDetailsPage"));
const MyServicesPage = lazy(() => import("@/pages/MyServicesPage"));
const MyCompaniesPage = lazy(() => import("@/pages/MyCompaniesPage"));
const ClientWorkflowDetailPage = lazy(() => import("@/pages/ClientWorkflowDetailPage"));
// const OneTaxTimelinePage = lazy(() => import("@/pages/OneTaxTimelinePage")); // Commented out - not being used
// const PortalTaxTimelinePage = lazy(() => import("@/pages/PortalTaxTimelinePage")); // Commented out - not being used
const DocumentManagerPage = lazy(() => import("@/pages/DocumentManagerPage"));
const SearchAndReportingPage = lazy(() => import("@/pages/SearchAndReportingPage"));
const OrgChartPage = lazy(() => import("@/pages/OrgChartPage"));
const ToolsPage = lazy(() => import("@/pages/ToolsPage"));
const BatchEmailPage = lazy(() => import("@/pages/BatchEmailPage"));
const BulkDocUploadPage = lazy(() => import("@/pages/BulkDocUploadPage"));
const BulkFupPage = lazy(() => import("@/pages/BulkFupPage"));
const ChangePasswordToolPage = lazy(() => import("@/pages/ChangePasswordToolPage"));
export const ROWS_PER_PAGE = 50;

declare global {
  interface Window {
    dataLayer: any[];
  }
}

function PageInfoTracker() {
  useNavigationCount();
  usePageViewTracker();
  return null;
}

function App(): JSX.Element {
  const [loading, setLoading] = useState<boolean>(true);
  const [lazoUser, setUser] = useState<LazoUser>();
  const [landingPage, setLandingPage] = useState<string | undefined>(undefined);
  const { setLazoUser } = useLazoUser();

  const loggedUserExternalId = authService.getToken();

  useEffect(() => {
    if (lazoUser) {
      setLazoUser(lazoUser);
    }
  }, [lazoUser]);

  useEffect(() => {
    const sessionTracked = sessionStorage.getItem("sessionTracked");
    if (!sessionTracked && !JSON.parse(sessionTracked) && lazoUser) {
      sessionStorage.setItem("sessionTracked", JSON.stringify(true));
      LazoUserSessionService.register({
        sessionEvent: LazoUserSessionEvents.LOGIN,
        userExternalId: lazoUser.externalId,
      });
    }
  }, [lazoUser]);

  useEffect(() => {
    authService.addExcludeAuthenticationChecks(PUBLIC_PATHS);

    const redirectToLoginIfSessionExpired = () => {
      const expiration = localStorage.getItem("t_expiration");
      if (!expiration || expiration.trim() === "") return;

      const expirationDate = moment(expiration);
      const today = moment();

      if (expirationDate < today) {
        authService.logOut();
        window.sessionStorage.setItem(PostLoginRedirectStorageKey, location.href);
        window.location.href = LazoPaths.LOGIN;
      }
    };

    const checkAuthentication = async () => {
      const isLoggedResponse = authService.isLogged();

      if (isLoggedResponse) {
        redirectToLoginIfSessionExpired();

        const loggedUser: User = authService.getUser();
        try {
          const lazoUser = (await LazoUserService.getByCoreUser()).getContentOrThrowError();
          setUser({ ...lazoUser });
          setLazoUser(lazoUser);
          setLandingPage(getLandingPageByUser(loggedUser));
          LAZO_APPLICATIONS_OPTIONS_SERVICE.initialize(
            lazoUser.externalId!,
            lazoUser.lazoApplicationOptions || ({} as LazoApplicationOptions)
          );

          const defaultRoute = getLandingPageByUser(loggedUser) || LazoPaths.DASHBOARD;

          authService.setPathToRedirectLoggedUser(defaultRoute);

          AuthService.redirectUserIfNeededDependingOnLoginState();
        } catch (error) {
          console.error("Failed to load user data:", error);

          const status = error && typeof error === "object" && "response" in error ? (error as any).response?.status : null;
          const isAuthError = status === 401 || status === 403;

          if (isAuthError) {
            authService.logOut();
            window.sessionStorage.setItem(PostLoginRedirectStorageKey, location.href);
            window.location.href = LazoPaths.LOGIN;
            return;
          }

          setLoading(false);
        }
      } else {
        const pathCanBeAccessedViaEmail = EMAIL_REDIRECT_PATHS.some((path) =>
          new RegExp(path.replace(":taskExternalId", "[^/]+")).test(location.pathname)
        );

        if (pathCanBeAccessedViaEmail) {
          window.sessionStorage.setItem(PostLoginRedirectStorageKey, location.href);
          window.location.href = LazoPaths.LOGIN;
        }
      }

      const error = authService.loginErrorJustHappened();
      if (error === LoginError.NOT_AUTHORIZED) {
        alert("You are not authorized");
      }

      if (error === LoginError.TOKEN_EXPIRED) {
        // Erase localStorage information, since the token has expired
        authService.logOut();
        alert("Your session has expired, please log in again");
      }

      setLoading(false);
    };

    if (!PUBLIC_PATHS.some((p) => matchPath(p, window.location.pathname) !== null)) {
      checkAuthentication();
    } else {
      setLoading(false);
    }
  }, [loggedUserExternalId]);

  if (loading) {
    return (
      <div style={{ position: "absolute", width: "100%", height: "100%" }}>
        <Spinner />
      </div>
    );
  }

  NotificationService.subscribe(NotificationEvents.ERROR, (message: string) => {
    const snackData: SnackBarData = { text: message, severity: "error" };
    sendSnackBarMessage(snackData);
  });

  NotificationService.subscribe(NotificationEvents.INFO, (message: string) => {
    const snackData: SnackBarData = { text: message, severity: "success" };
    sendSnackBarMessage(snackData);
  });

  NotificationService.subscribe(NotificationEvents.WARNING, (message: string) => {
    const snackData: SnackBarData = { text: message, severity: "warning" };
    sendSnackBarMessage(snackData);
  });

  authService.subscribe(AuthEvents.LOGOUT, () => {
    LocalStorageWorkspacesAndTenantMemberships.clearState();
    setUser(undefined);

    const existingConstants = new Map<string, string>();
    for (const c in LocalStorageConstants) {
      const key = LocalStorageConstants[c];
      existingConstants.set(key, localStorage.getItem(key));
    }

    localStorage.clear();
    existingConstants.forEach((v, k) => localStorage.setItem(k, v));

    for (const c in SessionStorageConstants) {
      const key = SessionStorageConstants[c];
      sessionStorage.removeItem(key);
    }

    window.location.href = LazoPaths.LOGIN;
  });

  return (
    <Router>
      {["qa", "dev"].includes(environment.env.toLowerCase()) && <EnvOverlay />}
      <AppProviders>
        <PageInfoTracker />
        <Routes>
          <Route
            element={
              <Suspense fallback={<Spinner sx={{ color: Colors.LAZO_PURPLE }} />}>
                <Outlet />
              </Suspense>
            }
          >
            <Route path={LazoPaths.INDEX} element={<GetStarted />} />

            <Route path={LazoPaths.REDIRECT} element={<RedirectPage setUser={(lu) => setUser(lu)} />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.SIGNUP].path} element={<Navigate to={LazoPaths.LOGIN} replace />} />
            <Route
              path={lazoRoutesSpecification[LazoRoutes.SIGNUP_COUNTRY_SELECT].path}
              element={<Navigate to={LazoPaths.LOGIN} replace />}
            />
            <Route
              path={lazoRoutesSpecification[LazoRoutes.SIGNUP_ACCOUNT_TYPE].path}
              element={<Navigate to={LazoPaths.LOGIN} replace />}
            />
            <Route path={lazoRoutesSpecification[LazoRoutes.SIGNUP_COMPANY].path} element={<Navigate to={LazoPaths.LOGIN} replace />} />
            <Route
              path={lazoRoutesSpecification[LazoRoutes.SIGNUP_COMPANY_INCORPORATION].path}
              element={<Navigate to={LazoPaths.LOGIN} replace />}
            />
            <Route path={lazoRoutesSpecification[LazoRoutes.SIGNUP_INDIVIDUAL].path} element={<Navigate to={LazoPaths.LOGIN} replace />} />
            <Route
              path={lazoRoutesSpecification[LazoRoutes.SIGNUP_COMPANY_INFO].path}
              element={<Navigate to={LazoPaths.LOGIN} replace />}
            />
            <Route
              path={lazoRoutesSpecification[LazoRoutes.SIGNUP_COMPANY_CONNECT].path}
              element={<Navigate to={LazoPaths.LOGIN} replace />}
            />

            <Route path={lazoRoutesSpecification[LazoRoutes.ONBOARDING].path} element={<LazoOnboarding />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.FORGOT_PASSWORD].path} element={<ForgotPasswordPage />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.FORGOT_PASSWORD_EMAIL_SENT].path} element={<ResetPasswordEmailSentPage />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.RESET_PASSWORD].path} element={<ResetPasswordPage />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.REGISTER].path} element={<PendingRegistrationPage />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.REGISTER_USER_FOR_COMPANY].path} element={<PersonalInformationPage />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.PERSONAL_INFORMATION].path} element={<PersonalInformationPage />} />

            <Route
              path={lazoRoutesSpecification[LazoRoutes.VERIFY_EMAIL].path}
              element={<EmailConfirmationStepComponent email={lazoUser?.user?.email} />}
            />

            <Route path={lazoRoutesSpecification[LazoRoutes.VERIFYING_EMAIL].path} element={<VerifyingEmailPage />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.LOGIN].path} element={<GetStarted />} />
            <Route path={lazoRoutesSpecification[LazoRoutes.NOT_SUPPORTED].path} element={<NotSupportedPage />} />
            <Route path={lazoRoutesSpecification[LazoRoutes.SHARE_BY_LINK].path} element={<GuestUserShareLinkOnboarding />} />
            <Route
              path={lazoRoutesSpecification[LazoRoutes.SHARED_VIRTUAL_DATA_ROOM].path}
              element={<SharedVirtualDataRoomsInsidePage />}
            />
            <Route
              path={lazoRoutesSpecification[LazoRoutes.SHARED_VIRTUAL_DATA_ROOM_FINANCIAL_REPORT].path}
              element={<SharedDataRoomFinancialReportPage />}
            />

            <Route
              path={lazoRoutesSpecification[LazoRoutes.SHARED_VIRTUAL_DATA_ROOMS_FILE].path}
              element={<SharedVirtualDataRoomsFilePage />}
            />

            <Route path={lazoRoutesSpecification[LazoRoutes.TERMS_AND_POLICIES].path} element={<TermsAndPolicies />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.INVITATION_COMPANY].path} element={<UserInvitationCompany />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.CHECKOUT].path} element={<CheckoutPage />} />

            <Route
              path={lazoRoutesSpecification[LazoRoutes.COMPANY_FORM].path}
              element={
                <ProtectedRoute>
                  <OnboardingPrototypeCompanyForm />
                </ProtectedRoute>
              }
            />

            <Route path={lazoRoutesSpecification[LazoRoutes.SHARED_CHECK_EMAIL].path} element={<SharedCheckEmailPage />} />

            <Route path={lazoRoutesSpecification[LazoRoutes.ROOM_EXPIRED].path} element={<RoomExpiredPage />} />
          </Route>
          <Route element={<ProtectedRoute expectedPermission="Lazo:Portal:Access" />}>
            <Route
              element={
                <TenantProvider>
                  <AppContainerWrapper lazoUser={lazoUser!} />
                </TenantProvider>
              }
            >
              <Route
                path={lazoRoutesSpecification[LazoRoutes.USER_MANAGEMENT].path}
                element={<UserManagement titleStyle={{ fontSize: "2rem" }} />}
              />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.TASKS_ADMIN].path}
                element={
                  <TaskFiltersProvider>
                    <AdminTasksPage />
                  </TaskFiltersProvider>
                }
              />
              <Route path={lazoRoutesSpecification[LazoRoutes.TASK_DETAILS].path} element={<TaskDetails />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.NEW_TASK].path} element={<NewTaskPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.LAZO_TEAM].path} element={<LazoTeamPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.LAZO_TEAM_DETAILS].path} element={<LazoEmployeeDetails />} />
              <Route element={<FiltersRoutesWrapper />}>
                <Route path={lazoRoutesSpecification[LazoRoutes.PORTAL_CUSTOMERS].path} element={<CustomersPage />} />
                <Route path={lazoRoutesSpecification[LazoRoutes.PORTAL_CUSTOMERS_DETAILS].path} element={<CustomerDetailsPage />} />
              </Route>
              <Route
                path={lazoRoutesSpecification[LazoRoutes.PORTAL_CUSTOMERS_ACCOUNTING].path}
                element={<CustomerDetailsAccountingPage />}
              />
              <Route element={<FiltersRoutesWrapper />}>
                <Route path={lazoRoutesSpecification[LazoRoutes.PRODUCT_MANAGER].path} element={<ProductManagerPage />} />
                <Route path={lazoRoutesSpecification[LazoRoutes.PRODUCT_FLOW_TASKS].path} element={<ProductFlowTasksPage />} />
                <Route path={lazoRoutesSpecification[LazoRoutes.PRODUCT_FLOW_TASKS_V2].path} element={<WorkflowDetailV2Page />} />
              </Route>
              <Route path={lazoRoutesSpecification[LazoRoutes.DOCUMENT_MANAGER].path} element={<DocumentManagerPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.SEARCH_AND_REPORTING].path} element={<SearchAndReportingPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ORG_CHART].path} element={<OrgChartPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.PORTAL_INBOX].path} element={<PortalInboxPage />} />
              {/* <Route path={lazoRoutesSpecification[LazoRoutes.PORTAL_TAX_TIMELINE].path} element={<PortalTaxTimelinePage />} /> */}{" "}
              {/* Commented out - not being used */}
              {/* TODO: Adapt notifications page to Portal */}
              <Route path={lazoRoutesSpecification[LazoRoutes.PORTAL_NOTIFICATIONS].path} element={<NotificationsPage />} />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.PORTAL_SETTINGS].path}
                element={<SettingsPage setLazoUser={setUser} lazoUser={lazoUser!} />}
              />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.ADMIN_TOOLS].path}
                //JUST APP ADMINISTRATORS SHOULD HAVE PERMISSIONS
                element={
                  <ProtectedRoute expectedPermission="App:Administration">
                    <AdminToolsPage />
                  </ProtectedRoute>
                }
              />
              <Route path={lazoRoutesSpecification[LazoRoutes.TOOLS].path} element={<ToolsPage />} />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.TOOLS_BATCH_EMAIL].path}
                element={
                  <TenantAwareProtectedRoute expectedPermissions={["Lazo:BatchEmail:Send", "App:Administration"]}>
                    <BatchEmailPage />
                  </TenantAwareProtectedRoute>
                }
              />
              <Route path={lazoRoutesSpecification[LazoRoutes.TOOLS_BULK_DOC_UPLOAD].path} element={<BulkDocUploadPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.TOOLS_BULK_FUP].path} element={<BulkFupPage />} />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.TOOLS_CHANGE_PASSWORD].path}
                element={
                  <TenantAwareProtectedRoute expectedPermissions={["Lazo:UserPassword:Change", "App:Administration"]}>
                    <ChangePasswordToolPage />
                  </TenantAwareProtectedRoute>
                }
              />
            </Route>
          </Route>

          <Route element={<ProtectedRoute expectedPermission="Lazo:One:Access" />}>
            <Route
              element={
                <TenantProvider>
                  <SubscriptionPurchaseProvider>
                    <AppContainerWrapper lazoUser={lazoUser!} />
                  </SubscriptionPurchaseProvider>
                </TenantProvider>
              }
            >
              <Route
                path={lazoRoutesSpecification[LazoRoutes.ONE_COMPANY_ADD].path}
                element={
                  <ProtectedRoute expectedPermission="Lazo:One:Company-Create">
                    <LazoOneCompanyAdd userExternalId={lazoUser?.user.externalId} />
                  </ProtectedRoute>
                }
              />
              <Route path={lazoRoutesSpecification[LazoRoutes.DASHBOARD].path} element={<StaticDashboard renderAppBar />} />
              {/* <Route path={lazoRoutesSpecification[LazoRoutes.HOME].path} element={<UserHome />} /> */}{" "}
              {/* Commented out - not being used, causes logout */}
              <Route path={lazoRoutesSpecification[LazoRoutes.DASHBOARD_SELECT].path} element={<DashboardSelection />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.TAX_DEADLINES].path} element={<DeadLineList deadlines={[]} />} />
              {/* <Route path={lazoRoutesSpecification[LazoRoutes.TAX_TIMELINE].path} element={<OneTaxTimelinePage />} /> */}{" "}
              {/* Commented out - not being used */}
              <Route path={lazoRoutesSpecification[LazoRoutes.DASHBOARD_DETAILS].path} element={<DashboardDetails />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.FUNDRAISING].path} element={<Navigate to={LazoPaths.TASKS} replace />} />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.FUNDRAISING_ADVANCED_SEARCH].path}
                element={<Navigate to={LazoPaths.TASKS} replace />}
              />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.FUNDER_LIST_DETAIL].path}
                element={<Navigate to={LazoPaths.TASKS} replace />}
              />
              {/* <Route path={lazoRoutesSpecification[LazoRoutes.USER_PROFILE].path} element={<UserProfile />} /> */}
              <Route path={lazoRoutesSpecification[LazoRoutes.INTEGRATIONS].path} element={<IntegrationsPage />} />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.TASKS].path}
                element={
                  <TaskFiltersProvider>
                    <TasksPage />
                  </TaskFiltersProvider>
                }
              />
              <Route path={lazoRoutesSpecification[LazoRoutes.PRODUCT].path} element={<Navigate to={LazoPaths.TASKS} replace />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.NOTIFICATIONS].path} element={<NotificationsPage />} />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.ONE_SETTINGS].path}
                element={<SettingsPage setLazoUser={setUser} lazoUser={lazoUser!} />}
              />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_TASK_DETAILS].path} element={<LazoOneTaskDetails />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_COMMUNICATIONS].path} element={<LazoOneChatRoot />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_DOCUMENT_CENTER].path} element={<OneDocumentCenterPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_DATA_ROOM].path} element={<OneDataRoomPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_DATA_ROOM_FILES].path} element={<OneDataRoomFilesPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_VIRTUAL_DATA_ROOMS].path} element={<OneVirtualDataRoomsPage />} />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.VIRTUAL_DATA_ROOM_FINANCIAL_REPORT].path}
                element={<VirtualDataRoomFinancialReportPage lazoUser={lazoUser} />}
              />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.ONE_VIRTUAL_DATA_ROOMS_CREATE].path}
                element={<OneVirtualDataRoomsCreatePage />}
              />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.ONE_VIRTUAL_DATA_ROOMS_INSIDE].path}
                element={<OneVirtualDataRoomsInsidePage />}
              />
              <Route
                path={lazoRoutesSpecification[LazoRoutes.ONE_VIRTUAL_DATA_ROOMS_FILE].path}
                element={<OneVirtualDataRoomsFilePage lazoUser={lazoUser} />}
              />
              <Route path={lazoRoutesSpecification[LazoRoutes.SPENDING].path} element={<OneSpendingPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_SPENDING_DETAILS].path} element={<OneSpendingDetailsPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.MY_SERVICES].path} element={<MyServicesPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.MY_COMPANIES].path} element={<MyCompaniesPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.MY_SERVICES_WORKFLOW_DETAIL].path} element={<ClientWorkflowDetailPage />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.VIRTUAL_ROOM_ANALYTICS].path} element={<VirtualRoomAnalytics />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_PAYWALL].path} element={<PaywallContent />} />
              <Route path={lazoRoutesSpecification[LazoRoutes.ONE_SIGNUP].path} element={<CreateCompanyOrIndividual />} />
            </Route>
          </Route>
          {/** Fallback and unauthorized routes */}

          <Route path="/logout" element={<Logout />} />
          <Route path={lazoRoutesSpecification[LazoRoutes.UNAUTHORIZED].path} element={<UnauthorizedScreen />} />

          <Route path="*" element={<Fallback landingPage={landingPage} />} />
        </Routes>
      </AppProviders>
    </Router>
  );
}

export default App;
